import {
  assign,
  createMachine,
  sendParent,
  fromPromise,
  assertEvent,
  enqueueActions,
} from 'xstate';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import { Address } from '../types';
import StripeScriptLoader from '../../stripe-script-loader';

export interface Context {
  organizationId?: string;
  clientSecret?: string;
  stripe?: Stripe;
  elements?: StripeElements;
  address: Address;
}

type Events =
  | { type: 'STRIPE_ELEMENTS_LOADED'; elements: StripeElements }
  | { type: 'ADDRESS_UPDATED'; address: Address; organizationId: string }
  | { type: 'NEXT' }
  | { type: 'RETRY' };

const paymentDetailsMachine = createMachine(
  {
    id: 'payment-details',
    types: {} as {
      context: Context;
      events: Events;
    },
    context: {
      organizationId: undefined,
      clientSecret: undefined,
      stripe: undefined,
      elements: undefined,
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        country: 'US',
        postal_code: '',
      },
    },
    initial: 'initializingStripe',
    on: {
      ADDRESS_UPDATED: {
        actions: [
          'assignAddress',
          'assignOrganizationId',
        ],
      },
      STRIPE_ELEMENTS_LOADED: {
        actions: 'assignElements',
      },
    },
    states: {
      initializingStripe: {
        type: 'parallel',
        states: {
          loadingStripe: {
            initial: 'loading',
            states: {
              loading: {
                invoke: {
                  src: 'fetchStripe',
                  onDone: {
                    target: 'done',
                    actions: assign({ stripe: ({ event }) => event.output }),
                  },
                  onError: {
                    target: '#payment-details.stripeLoadError',
                    actions: [
                      'notifyConnectionError',
                      ({ event }) => console.error(event.error),
                    ],
                  },
                },
              },
              done: {
                type: 'final',
              },
            },
          },
          loadingClientSecret: {
            initial: 'loading',
            states: {
              loading: {
                invoke: {
                  src: 'fetchStripeClientSecret',
                  onDone: {
                    target: 'done',
                    actions: assign({ clientSecret: ({ event }) => event.output }),
                  },
                  onError: {
                    target: '#payment-details.stripeLoadError',
                    actions: [
                      'notifyConnectionError',
                      ({ event }) => console.error(event.error),
                    ],
                  },
                },
              },
              done: {
                type: 'final',
              },
            },
          },
        },
        onDone: 'gettingAddress',
      },
      stripeLoadError: {
        on: {
          RETRY: 'initializingStripe',
        },
      },
      gettingAddress: {
        on: {
          NEXT: {
            guard: 'hasAddress',
            target: 'gettingPaymentDetails',
          },
        },
      },
      gettingPaymentDetails: {
        on: {
          NEXT: {
            guard: 'hasElements',
            target: 'submittingPaymentDetails',
          },
        },
      },
      submittingPaymentDetails: {
        invoke: {
          src: 'submitPaymentDetails',
          input: ({ context }) => context,
          onDone: {
            target: 'gettingPaymentDetails',
            actions: 'notifyPaymentDetailsDone',
          },
          onError: {
            target: 'gettingPaymentDetails',
            actions: [
              'notifySubmitPaymentDetailsError',
              ({ event }) => console.error(event.error),
            ],
          },
        },
      },
    },
  },
  {
    actions: {
      assignElements: assign({
        elements: ({ event }) => {
          assertEvent(event, 'STRIPE_ELEMENTS_LOADED');
          return event.elements;
        },
      }),
      assignAddress: assign({
        address: ({ event }) => {
          assertEvent(event, 'ADDRESS_UPDATED');
          return event.address;
        },
      }),
      assignOrganizationId: assign({
        organizationId: ({ event }) => {
          assertEvent(event, 'ADDRESS_UPDATED');
          return event.organizationId;
        },
      }),
      notifyPaymentDetailsDone: sendParent({ type: 'PAYMENT_DETAILS_DONE' }),
      notifyConnectionError: sendParent({
        type: 'NOTIFY_ERROR',
        error: new Error('Connection Error. Please try again.'),
      }),
      notifySubmitPaymentDetailsError: enqueueActions(({ enqueue, event }) => {
        const errorEvent = event as unknown as { error: unknown };
        const errorMessage = (errorEvent.error instanceof Error ? errorEvent.error.message : String(errorEvent.error))
          || 'Error adding payment details to stripe';
        enqueue.sendParent({
          type: 'NOTIFY_ERROR',
          error: new Error(errorMessage),
        });
      }),
    },
    guards: {
      hasElements: ({ context }) => !!context.elements,
      hasAddress: ({ context }) => (
        context.address.line1.length > 0
        && context.address.city.length > 0
        && context.address.state.length > 0
        && context.address.country.length > 0
        && context.address.postal_code.length > 0
      ),
    },
    actors: {
      fetchStripe: fromPromise(async () => StripeScriptLoader.instance.stripePromise),
      fetchStripeClientSecret: fromPromise(async () => {
        const response = await fetch('/api/_private/stripe/client_secret');
        const { client_secret: clientSecret } = await response.json();
        return clientSecret;
      }),
      submitPaymentDetails: fromPromise(async ({ input }: { input: Context }) => {
        const {
          stripe,
          elements,
          address,
          organizationId,
        } = input;

        const [stripeConfirmSetupResult, addressResult] = await Promise.all([
          (!stripe
            ? Promise.reject(new Error('Unable to confirm payment details'))
            : stripe.confirmSetup({
              elements: elements as StripeElements,
              confirmParams: {
                payment_method_data: {
                  billing_details: {
                    address,
                  },
                },
              },
              redirect: 'if_required',
            })
          ),
          fetch(`/api/_private/organizations/${organizationId}/address`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              organization: {
                street_address: address.line1,
                extended_address: address.line2,
                locality: address.city,
                region: address.state,
                country: address.country,
                postal_code: address.postal_code,
              },
            }),
          }),
        ]);
        if (stripeConfirmSetupResult?.error) {
          throw stripeConfirmSetupResult.error;
        }
        if (!addressResult.ok) {
          throw new Error('Failed to update address');
        }
      }),
    },
  },
);

export default paymentDetailsMachine;
